/**
 * PriceEstimateCard — Displays an AI-generated price estimate with range bar,
 * confidence badge, factors, comparables, and a "Use Recommended" action.
 */

import React, { useState } from 'react';
import './PriceEstimateCard.css';

const CONFIDENCE_META = {
  high:   { label: 'High',   dots: 3, className: 'high' },
  medium: { label: 'Medium', dots: 2, className: 'medium' },
  low:    { label: 'Low',    dots: 1, className: 'low' },
};

// Exported so callers that mirror these same numbers elsewhere (e.g. the
// ListingForm live-region announcement) format them identically.
export function formatPrice(value) {
  if (value == null) return '—';
  return '$' + Number(value).toLocaleString('en-US', { maximumFractionDigits: 0 });
}

export default function PriceEstimateCard({
  estimate,
  confidence,
  factors,
  comparables,
  disclaimer,
  loading,
  error,
  onUsePrice,
  onRetry,
}) {
  const [showComps, setShowComps] = useState(false);

  if (loading) {
    return (
      <div
        className="price-estimate price-estimate--loading"
        role="status"
        data-testid="price-estimate-loading"
      >
        <div className="price-estimate__skeleton-bar" />
        <div className="price-estimate__skeleton-text" />
        <div className="price-estimate__skeleton-text price-estimate__skeleton-text--short" />
        {/* No market data is fetched, and "value" is appraisal language — the
            output is a suggested range (legal review). */}
        <span className="price-estimate__loading-label">Generating a suggested price range…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="price-estimate price-estimate--error"
        role="alert"
        data-testid="price-estimate-error"
      >
        <span className="price-estimate__error-icon">⚠️</span>
        <p className="price-estimate__error-msg">{error}</p>
        {onRetry && (
          <button type="button" className="price-estimate__retry-btn" onClick={onRetry}>
            Try Again
          </button>
        )}
      </div>
    );
  }

  if (!estimate) return null;

  const conf = CONFIDENCE_META[confidence] || CONFIDENCE_META.medium;
  const range = estimate.high - estimate.low;
  const recOffset = range > 0
    ? ((estimate.recommended - estimate.low) / range) * 100
    : 50;

  return (
    <div className="price-estimate" data-testid="price-estimate-card">
      <div className="price-estimate__header">
        <span className="price-estimate__sparkle">✨</span>
        <h4 className="price-estimate__title">AI Price Estimate</h4>
        {/* The model rates its own confidence; there is no statistical basis
            behind it, so say whose rating it is. */}
        <span
          className={`price-estimate__confidence price-estimate__confidence--${conf.className}`}
          title="The AI's own rating of how sure it is. Not a statistical measure."
        >
          {'●'.repeat(conf.dots)}{'○'.repeat(3 - conf.dots)} {conf.label} (AI&apos;s own rating)
        </span>
      </div>

      {/* Range bar */}
      <div className="price-estimate__range">
        <div className="price-estimate__range-labels">
          <span className="price-estimate__range-low">{formatPrice(estimate.low)}</span>
          <span className="price-estimate__range-high">{formatPrice(estimate.high)}</span>
        </div>
        <div className="price-estimate__range-track">
          <div
            className="price-estimate__range-marker"
            style={{ left: `${Math.min(Math.max(recOffset, 5), 95)}%` }}
            data-testid="price-estimate-marker"
          >
            <span className="price-estimate__range-rec-label">{formatPrice(estimate.recommended)}</span>
          </div>
        </div>
        <div className="price-estimate__range-legend">
          <span>Low</span>
          <span>Suggested</span>
          <span>High</span>
        </div>
      </div>

      {/* Use Suggested Price button — "suggested", not "recommended": the
          output is not a broker's price opinion (legal review). */}
      {onUsePrice && (
        <button
          type="button"
          className="price-estimate__use-btn"
          onClick={() => onUsePrice(estimate.recommended)}
          data-testid="price-estimate-use-btn"
        >
          Use Suggested Price
        </button>
      )}

      {/* Factors */}
      {factors && factors.length > 0 && (
        <div className="price-estimate__factors">
          <h5 className="price-estimate__factors-title">Key Factors</h5>
          <ul className="price-estimate__factors-list">
            {factors.map((f, i) => (
              <li key={i}>{f}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Example homes (collapsible). The model makes these up — they are not
          real properties, listings or sales — so the label says so even while
          collapsed, prices are "illustrative", and a street address is never
          shown, even from estimates saved before the prompt stopped asking for
          one (a made-up address can be someone's real home). Legal review. */}
      {comparables && comparables.length > 0 && (
        <div className="price-estimate__comps">
          <button
            type="button"
            className="price-estimate__comps-toggle"
            aria-expanded={showComps}
            onClick={() => setShowComps((v) => !v)}
          >
            {showComps ? '▾' : '▸'} Hypothetical example homes ({comparables.length}) — not real sales
          </button>
          {showComps && (
            <div className="price-estimate__comps-list">
              <p className="price-estimate__comps-note" data-testid="price-estimate-comps-note">
                These are hypothetical homes the AI made up to show how size and features
                can affect price. They are not real properties, listings, or sales, and
                they are not based on sales data.
              </p>
              {comparables.map((c, i) => (
                <div className="price-estimate__comp" key={i}>
                  <span className="price-estimate__comp-label">Example {i + 1}</span>
                  <span className="price-estimate__comp-price">
                    <span className="price-estimate__comp-price-label">Illustrative price</span>{' '}
                    {formatPrice(c.price)}
                  </span>
                  <span className="price-estimate__comp-details">
                    {[c.beds && `${c.beds}bd`, c.baths && `${c.baths}ba`, c.sqft && `${c.sqft.toLocaleString()}sf`]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                  <p className="price-estimate__comp-desc">{c.description}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Disclaimer */}
      {disclaimer && (
        <p className="price-estimate__disclaimer" data-testid="price-estimate-disclaimer">
          ⚠️ {disclaimer}
        </p>
      )}
    </div>
  );
}
